import { Button, Flex, Spinner, Text, useDisclosure } from "@chakra-ui/react";
import React, { useContext } from "react";
import { FiTrash2 } from "react-icons/fi";
import {
  PageContainer,
  PageHeadingContainer,
} from "../components/atoms/Containers";

import { PageHeading } from "../components/atoms/Headings";
import { ChangePasswordModal } from "../components/molecules/forms/settingForms/ChangePasswordModal";
import { EditDisplayNameModal } from "../components/molecules/forms/settingForms/EditDisplayNameModal";
import { UserContext } from "../contexts/UserContext";
import { isProduction } from "../helper/environment";
import { useQueryGetUser } from "../store/useQueryGetUser";
import { User } from "../types/user/user";
import { EditValueSettingRow } from "./accountSettings/components/EditValueSettingRow";
import { ReadOnlySettingRow } from "./accountSettings/components/ReadOnlySettingRow";
import { SettingRowBase } from "./accountSettings/components/SettingRowBase";
import {
  SectionHeader,
  SectionContainer,
  SettingsStack,
} from "./accountSettings/components/SettingsComponents";
import { DeleteUserModal } from "./userSettings/DeleteUserModal";

export const UserSettings = () => {
  return (
    <PageContainer>
      <PageHeadingContainer>
        <PageHeading>User Profile</PageHeading>
      </PageHeadingContainer>
      <SectionContainer>
        <SectionHeader title="User profile settings" />
        <SettingsSection />
      </SectionContainer>
    </PageContainer>
  );
};

function SettingsSection() {
  const { userDetails } = useContext(UserContext);

  const queryGetUser = useQueryGetUser();

  if (queryGetUser.isLoading)
    return (
      <Flex justifyContent="center" my="40px">
        <Spinner />
      </Flex>
    );

  if (!queryGetUser.data)
    return (
      <Flex alignItems="center" direction="column" my="40px">
        <Text textAlign="center">
          There was an error fetching user data, try again.
        </Text>
        <Button
          colorScheme="blue"
          onClick={() => {
            queryGetUser.refetch();
          }}
          size="sm"
          variant="link"
          w="100px"
        >
          Refresh
        </Button>
      </Flex>
    );

  return (
    <SettingsStack>
      <ReadOnlySettingRow label="Email" value={userDetails?.email ?? ""} />
      <PasswordSettingRow />
      <DisplayNameSettingRow user={queryGetUser.data} />
      {!isProduction() && <DeleteUserSettingRow />}
    </SettingsStack>
  );
}

function PasswordSettingRow() {
  const disclosure = useDisclosure();

  return (
    <>
      <EditValueSettingRow
        label="Password"
        value="******"
        action={disclosure.onOpen}
        canEdit={true}
      />
      {disclosure.isOpen && <ChangePasswordModal disclosure={disclosure} />}
    </>
  );
}

function DisplayNameSettingRow({ user }: { user: User }) {
  const disclosure = useDisclosure();

  return (
    <>
      <EditValueSettingRow
        label="User display name"
        description="Your display name. People you share account access with can see this."
        value={user.fullName}
        action={disclosure.onOpen}
        canEdit={true}
      />
      {disclosure.isOpen && (
        <EditDisplayNameModal
          currentFullName={user.fullName || ""}
          disclosure={disclosure}
        />
      )}
    </>
  );
}

function DeleteUserSettingRow() {
  const disclosure = useDisclosure();

  return (
    <>
      <SettingRowBase
        label="Delete user"
        description="Permanently delete your user profile, including any attached accounts and purchase history belonging to you. This action is irreversible."
      >
        <Button
          colorScheme="red"
          leftIcon={<FiTrash2 />}
          onClick={disclosure.onOpen}
        >
          Delete user
        </Button>
      </SettingRowBase>
      {disclosure.isOpen && <DeleteUserModal disclosure={disclosure} />}
    </>
  );
}
